import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { RevenueExpenseData } from '../../hooks/useChartsData';

interface RevenueExpenseChartProps {
  data: RevenueExpenseData[];
  showRevenue?: boolean;
  showExpenses?: boolean;
  formatAmount?: (amount: number) => string;
  formatCompactAmount?: (amount: number) => string;
}

export function RevenueExpenseChart({ 
  data, 
  showRevenue = true, 
  showExpenses = true,
  formatAmount,
  formatCompactAmount
}: RevenueExpenseChartProps) {
  // Les formatters par défaut (utilisés seulement si aucune prop n'est passée)
  const defaultFormatAmount = (amount: number) => {
    return new Intl.NumberFormat('fr-CD', { 
      style: 'currency', 
      currency: 'CDF',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const defaultFormatCompact = (value: number) => {
    if (value >= 1000000000) {
      return `${(value / 1000000000).toFixed(1)}Md FC`;
    } else if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M FC`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}k FC`;
    }
    return `${value} FC`;
  };

  const amountFormatter = formatAmount || defaultFormatAmount;
  const compactFormatter = formatCompactAmount || defaultFormatCompact;
  
  return (
    <ResponsiveContainer width="100%" height={350}>
      <AreaChart 
        data={data} 
        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
      >
        <defs>
          {/* Gradient pour le chiffre d'affaires (vert) */}
          <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
            <stop offset="95%" stopColor="#22c55e" stopOpacity={0.1}/>
          </linearGradient>
          
          {/* Gradient pour les charges (rouge pâle) */}
          <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#ef4444" stopOpacity={0.2}/>
            <stop offset="95%" stopColor="#ef4444" stopOpacity={0.05}/>
          </linearGradient>
        </defs>
        
        <CartesianGrid strokeDasharray="3 3" vertical={false} className="opacity-30" />
        
        <XAxis 
          dataKey="date" 
          tickFormatter={(value) => {
            const date = new Date(value);
            return date.toLocaleDateString('fr-FR', { 
              month: 'short',
              year: data.length > 12 ? undefined : '2-digit'
            });
          }}
          className="text-xs"
        />
        
        <YAxis 
          tickFormatter={compactFormatter}
          className="text-xs"
        />
        
        <Tooltip 
          formatter={(value: number, name: string) => [
            amountFormatter(value),
            name === 'revenue' ? 'Chiffre d\'affaires' : 'Total des charges'
          ]}
          labelFormatter={(label) => {
            const date = new Date(label);
            return date.toLocaleDateString('fr-FR', {
              year: 'numeric',
              month: 'long'
            });
          }}
          contentStyle={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '14px'
          }}
        />
        
        <Legend 
          wrapperStyle={{ fontSize: '14px', paddingTop: '10px' }}
        />
        
        {/* Area pour le chiffre d'affaires */}
        {showRevenue && (
          <Area 
            type="monotone" 
            dataKey="revenue" 
            stackId="1"
            stroke="#22c55e" 
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorRevenue)"
            name="Chiffre d'affaires"
          />
        )}
        
        {/* Area pour les charges */}
        {showExpenses && (
          <Area 
            type="monotone" 
            dataKey="expenses" 
            stackId="2"
            stroke="#ef4444" 
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorExpenses)"
            name="Total des charges"
          />
        )}
      </AreaChart>
    </ResponsiveContainer>
  );
}